import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartAddEvent } from '@theme/events';

/**
 * Escapes a string for safe use as HTML text content.
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value ?? '';
  return div.innerHTML;
}

/**
 * Coerces a price from the `/recommendations/products.json` endpoint to minor units (cents).
 *
 * Like the Ajax API, that endpoint already returns variant prices as integers in minor units
 * (e.g. 295000 for ₴2,950.00), so we just coerce to a number — multiplying by 100 here would
 * inflate every price ~100×.
 * @param {string | number | null | undefined} value
 * @returns {number}
 */
function toCents(value) {
  if (value === null || value === undefined) return 0;
  const numeric = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(numeric)) return 0;
  return Math.round(numeric);
}

/**
 * Formats a price (minor units / cents) the same way `snippets/price.liquid` does:
 * decimals dropped, thousands grouped with spaces, and the currency symbol appended
 * (e.g. 265000 -> "2 650 ₴").
 * @param {number} cents
 * @param {string} symbol - The currency symbol, passed in from Liquid.
 * @returns {string}
 */
function formatPrice(cents, symbol) {
  const major = Math.trunc(cents / 100);
  const grouped = major.toLocaleString('en-US').replace(/,/g, ' ');
  return symbol ? `${grouped} ${symbol}` : grouped;
}

/**
 * Renders the "picks to go with your order" carousel inside the cart drawer,
 * sourced from Shopify's standard product recommendations API.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} list
 * @property {HTMLButtonElement} prevButton
 * @property {HTMLButtonElement} nextButton
 *
 * @extends {Component<Refs>}
 */
class CartUpsellComponent extends Component {
  requiredRefs = ['list', 'prevButton', 'nextButton'];

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {AbortController | null} Lifecycle controller for listeners bound to the list element. */
  #listController = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number} requestAnimationFrame id for the post-drag momentum/glide animation. */
  #momentumId = 0;

  /**
   * Last successfully rendered recommendations, kept so the carousel can be restored
   * instantly (no refetch) if a section morph ever clears its contents.
   * @type {Array<Record<string, any>>}
   */
  #products = [];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#setupCarousel();
    this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#controller?.abort();
    this.#listController?.abort();
    this.#resizeObserver?.disconnect();
    if (this.#momentumId) cancelAnimationFrame(this.#momentumId);
  }

  /**
   * Wires up the carousel interactions on the list element.
   *
   * Note: Horizon's event delegation (component.js) does not delegate `scroll`, so an
   * `on:scroll` attribute would never fire (this was why the arrow disabled-state used to be
   * stuck). We bind the scroll listener directly here instead. A ResizeObserver recomputes the
   * arrow state once the list actually has a width (e.g. when the closed drawer is first
   * opened), and pointer handlers add click-and-drag scrolling with a mouse.
   */
  #setupCarousel() {
    const { list } = this.refs;
    if (!list) return;

    this.#listController?.abort();
    this.#listController = new AbortController();
    const { signal } = this.#listController;

    list.addEventListener('scroll', () => this.#updateArrowsState(), { passive: true, signal });

    this.#resizeObserver = new ResizeObserver(() => this.#updateArrowsState());
    this.#resizeObserver.observe(list);

    this.#setupDragScroll(list, signal);
  }

  /**
   * Adds click-and-drag (mouse) scrolling to the carousel, with a momentum glide + smooth
   * snap on release so it animates instead of stopping dead. Touch keeps native scrolling.
   * @param {HTMLElement} list
   * @param {AbortSignal} signal
   */
  #setupDragScroll(list, signal) {
    let isDragging = false;
    let didMove = false;
    let startX = 0;
    let startScrollLeft = 0;
    let lastX = 0;
    let lastTime = 0;
    let velocity = 0; // scroll px per ms (sign follows scrollLeft)

    list.addEventListener(
      'pointerdown',
      (event) => {
        // Leave touch/pen to the browser's native momentum scrolling.
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (event.target.closest('button') || event.target.closest('form') || event.target.closest('a')) {
          return;
        }

        if (this.#momentumId) cancelAnimationFrame(this.#momentumId);
        this.#momentumId = 0;
        isDragging = true;
        didMove = false;
        velocity = 0;
        startX = lastX = event.clientX;
        lastTime = event.timeStamp;
        startScrollLeft = list.scrollLeft;
        list.setPointerCapture(event.pointerId);
        list.classList.add('cart-upsell__list--dragging');
      },
      { signal }
    );

    list.addEventListener(
      'pointermove',
      (event) => {
        if (!isDragging) return;
        const delta = event.clientX - startX;
        if (Math.abs(delta) > 3) didMove = true;
        list.scrollLeft = startScrollLeft - delta;

        // Track instantaneous velocity for the release glide.
        const dt = event.timeStamp - lastTime;
        if (dt > 0) {
          velocity = -(event.clientX - lastX) / dt;
          lastX = event.clientX;
          lastTime = event.timeStamp;
        }
      },
      { signal }
    );

    const endDrag = (/** @type {PointerEvent} */ event) => {
      if (!isDragging) return;
      isDragging = false;
      if (list.hasPointerCapture(event.pointerId)) list.releasePointerCapture(event.pointerId);

      if (!didMove) {
        list.classList.remove('cart-upsell__list--dragging');
        return;
      }

      // Glide with the release velocity, decaying it each frame, then smooth-snap to the
      // nearest card. The cards stay non-interactive (dragging class) until the animation
      // settles, which also swallows the trailing click so a drag never opens a product.
      const friction = 0.92;
      const minVelocity = 0.05;

      const glide = () => {
        list.scrollLeft += velocity * 16;
        velocity *= friction;

        if (Math.abs(velocity) > minVelocity) {
          this.#momentumId = requestAnimationFrame(glide);
          return;
        }

        this.#momentumId = 0;
        this.#settleAfterDrag();
      };

      this.#momentumId = requestAnimationFrame(glide);
    };

    list.addEventListener('pointerup', endDrag, { signal });
    list.addEventListener('pointercancel', endDrag, { signal });
  }

  /**
   * Re-seeds the carousel from the most recently added product whenever the cart changes.
   * If the seed is unchanged (e.g. a quantity tweak) we don't refetch, but we do make sure a
   * section morph didn't wipe the already-rendered carousel.
   * @param {CartAddEvent | import('@theme/events').CartUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    const items = event.detail?.resource?.items;
    const newSeedId = Array.isArray(items) ? items.at(-1)?.product_id : undefined;

    const isOwnUpsellAdd = event.detail?.data?.isUpsell;
    const isDeletedFromCart = event.detail?.data?.source === 'cart-items-component';

    if (isOwnUpsellAdd || isDeletedFromCart) {
      this.#products = [];
      this.#load();
      return;
    }

    if (newSeedId && String(newSeedId) !== this.dataset.productId) {
      this.dataset.productId = String(newSeedId);
      this.#load();
      return;
    }

    // Restore after any synchronous morph triggered by the same cart update has run.
    requestAnimationFrame(() => this.#restoreIfCleared());
  };

  /**
   * Re-renders from the cached recommendations if the list was emptied or re-hidden
   * (defensive fallback in case the component's content is ever cleared by a re-render).
   */
  #restoreIfCleared() {
    if (!this.#products.length) return;

    const list = this.refs.list;
    const isCleared = !list || list.childElementCount === 0;

    if (isCleared || this.classList.contains('hidden')) {
      this.#render(this.#products);
    }
  }

  async #load() {
    const { url, productId, limit } = this.dataset;
    if (!url || !productId) return;

    this.#controller?.abort();
    this.#controller = new AbortController();

    try {
      // Complementary recommendations require the merchant to have cross-sell
      // recommendations configured (Search & Discovery app). Fall back to the
      // built-in "related" model if that returns nothing.
      let products = await this.#fetchRecommendations(url, productId, limit, 'complementary', this.#controller.signal);

      if (!products.length) {
        products = await this.#fetchRecommendations(url, productId, limit, 'related', this.#controller.signal);
      }

      this.#render(products);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error('Cart upsell error:', error);
      this.classList.add('hidden');
    }
  }

  /**
   * @param {string} url
   * @param {string} productId
   * @param {string | undefined} limit
   * @param {string} intent
   * @param {AbortSignal} signal
   * @returns {Promise<Array<Record<string, any>>>}
   */
  async #fetchRecommendations(url, productId, limit, intent, signal) {
    const response = await fetch(`${url}.json?product_id=${productId}&limit=${limit || 6}&intent=${intent}`, {
      signal,
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const data = await response.json();
    return Array.isArray(data.products) ? data.products : [];
  }

  /** @param {Array<Record<string, any>>} products */
  #render(products) {
    if (!products.length) {
      this.classList.add('hidden');
      return;
    }

    this.#products = products;
    this.refs.list.innerHTML = products.map((product) => this.#renderCard(product)).join('');
    this.classList.remove('hidden');
    this.#updateArrowsState();
  }

  /** @param {Record<string, any>} product */
  #renderCard(product) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const firstAvailable = variants.find((variant) => variant.available) || variants[0];
    const hasMultipleVariants = variants.length > 1;

    const currencySymbol = this.dataset.currencySymbol || '';
    const priceCents = toCents(firstAvailable?.price ?? product.price);
    const compareAtPriceCents = toCents(firstAvailable?.compare_at_price);
    const price = formatPrice(priceCents, currencySymbol);
    const showCompareAtPrice = compareAtPriceCents > priceCents;

    const image = product.featured_image || (Array.isArray(product.images) ? product.images[0] : null);
    const title = escapeHtml(product.title);

    const options = variants
      .map(
        (variant) =>
          `<option value="${variant.id}" ${variant.id === firstAvailable?.id ? 'selected' : ''} ${
            !variant.available ? 'disabled' : ''
          }>${escapeHtml(variant.public_title || variant.title)}</option>`
      )
      .join('');

    const selectMarkup = hasMultipleVariants
      ? `<select class="cart-upsell-card__select" name="cart-upsell-card__select" ref="select" aria-label="${title}">${options}</select>`
      : `<span class="cart-upsell-card__select cart-upsell-card__select--static">${escapeHtml(
          firstAvailable?.public_title || firstAvailable?.title || ''
        )}</span>`;

    return `
      <li class="cart-upsell-card" data-testid="cart-upsell-card">
        <cart-upsell-item class="cart-upsell-item" data-variant-id="${firstAvailable?.id ?? ''}" data-product-id="${
          product.id
        }">
          <a class="cart-upsell-card__media" href="${product.url}" tabindex="-1">
            ${image ? `<img src="${image}" alt="${title}" loading="lazy" width="200" height="250">` : ''}
          </a>
          <a class="cart-upsell-card__title" href="${product.url}">${title}</a>
          <div class="cart-upsell-card__price">
            <span class="cart-upsell-card__current-price price">${price}</span>
            ${
              showCompareAtPrice
                ? `<s class="cart-upsell-card__compare-price compare-at-price">${formatPrice(
                    compareAtPriceCents,
                    currencySymbol
                  )}</s>`
                : ''
            }
          </div>
          <div class="cart-upsell-card__form">
            ${selectMarkup}
            <button
              type="button"
              class="cart-upsell-card__add"
              ref="submitButton"
              on:click="/addToCart"
              aria-label="${Theme.translations.add_to_cart}: ${title}"
              ${!firstAvailable?.available ? 'disabled' : ''}
            ></button>
          </div>
        </cart-upsell-item>
      </li>
    `;
  }

  scrollPrev() {
    this.refs.list.scrollBy({ left: -this.#scrollStep(), behavior: 'smooth' });
  }

  scrollNext() {
    this.refs.list.scrollBy({ left: this.#scrollStep(), behavior: 'smooth' });
  }

  /**
   * Width of a single card including the inter-card gap (one "snap unit").
   * @returns {number}
   */
  #cardUnit() {
    const { list } = this.refs;
    const card = list.firstElementChild;
    if (!(card instanceof HTMLElement)) return 0;

    const styles = getComputedStyle(list);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return card.getBoundingClientRect().width + gap;
  }

  /**
   * Distance to advance per arrow click: the number of fully visible cards, so the partially
   * visible "peek" card becomes the new leading card.
   * @returns {number}
   */
  #scrollStep() {
    const { list } = this.refs;
    const unit = this.#cardUnit();
    if (unit <= 0) return list.clientWidth;

    const visibleCards = Math.max(1, Math.floor(list.clientWidth / unit));
    return unit * visibleCards;
  }

  /** Smoothly snaps the carousel to the nearest card after a drag/glide. */
  #snapToNearest() {
    const { list } = this.refs;
    const unit = this.#cardUnit();
    if (unit <= 0) return;

    const target = Math.round(list.scrollLeft / unit) * unit;
    list.scrollTo({ left: target, behavior: 'smooth' });
  }

  /**
   * Finishes a drag: smooth-snaps to the nearest card, then restores interactivity and CSS
   * snapping only once the snap has settled. Keeping the dragging class on until `scrollend`
   * avoids re-enabling `scroll-snap-type: mandatory` mid-animation (which would jump-cut the
   * smooth snap), and keeps the trailing click swallowed so a drag never opens a product.
   */
  #settleAfterDrag() {
    const { list } = this.refs;
    if (!list) return;

    this.#snapToNearest();

    const finish = () => {
      clearTimeout(timer);
      list.removeEventListener('scrollend', finish);
      list.classList.remove('cart-upsell__list--dragging');
    };
    // `scrollend` fires when the smooth snap completes; the timeout covers a zero-distance
    // snap (no scroll event) and browsers without `scrollend` support.
    const timer = setTimeout(finish, 500);
    list.addEventListener('scrollend', finish, { once: true });
  }

  #updateArrowsState() {
    const { list, prevButton, nextButton } = this.refs;
    if (!list || !prevButton || !nextButton) return;

    const maxScrollLeft = list.scrollWidth - list.clientWidth;

    // No room to scroll (few recommendations): disable both arrows.
    if (maxScrollLeft <= 1) {
      prevButton.disabled = true;
      nextButton.disabled = true;
      return;
    }

    prevButton.disabled = list.scrollLeft <= 1;
    nextButton.disabled = list.scrollLeft >= maxScrollLeft - 1;
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}

/**
 * A single upsell card that adds its selected variant to the cart via AJAX.
 *
 * @typedef {object} ItemRefs
 * @property {HTMLSelectElement | undefined} select
 * @property {HTMLButtonElement} submitButton
 *
 * @extends {Component<ItemRefs>}
 */
class CartUpsellItem extends Component {
  requiredRefs = ['submitButton'];

  /** @param {Event} event */
  async addToCart(event) {
    event.preventDefault();

    const variantId = this.refs.select?.value || this.dataset.variantId;
    if (!variantId || this.refs.submitButton.disabled) return;

    this.refs.submitButton.disabled = true;
    this.classList.add('cart-upsell-item--loading');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
      .filter(Boolean);

    const body = JSON.stringify({
      id: Number(variantId),
      quantity: 1,
      sections: sectionIds.join(','),
    });

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const result = await response.json();

      if (result.status) {
        this.dispatchEvent(
          new CartAddEvent(
            {},
            this.dataset.productId,
            { didError: true, source: 'cart-upsell-item' }
          )
        );
        return;
      }

      this.dispatchEvent(
        new CartAddEvent(result, this.dataset.productId, {
          source: 'product-form-component',
          itemCount: 1,
          productId: this.dataset.productId,
          variantId,
          sections: result.sections,
          isUpsell: true,
        })
      );

      this.classList.add('cart-upsell-item--added');
      setTimeout(() => this.classList.remove('cart-upsell-item--added'), 1500);
    } catch (error) {
      console.error('Cart upsell add to cart error:', error);
    } finally {
      this.refs.submitButton.disabled = false;
      this.classList.remove('cart-upsell-item--loading');
    }
  }
}

if (!customElements.get('cart-upsell-item')) {
  customElements.define('cart-upsell-item', CartUpsellItem);
}
